import React, { lazy, Suspense, useEffect } from 'react';
    import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
    import { AuthProvider, useAuth } from '@/contexts/AuthContext';
    import { DebugProvider } from '@/contexts/DebugContext';
    import { DebugConsole } from '@/components/ui/DebugConsole';
    import { Toaster, toast } from 'react-hot-toast';
    import { MainLayout } from '@/components/layout/MainLayout';
    import { AuthLayout } from '@/components/layout/AuthLayout';
    import { DashboardLayout } from '@/components/layout/DashboardLayout';
    import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

    import { LandingPage } from '@/pages/landing/LandingPage';
    import { LoginPage } from '@/pages/auth/LoginPage';
    import { RegisterPage } from '@/pages/auth/RegisterPage';
    import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
    import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
    import { UnauthorizedPage } from '@/pages/auth/UnauthorizedPage';
    import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
    import { BusinessDashboard } from '@/pages/dashboard/BusinessDashboard';
    
    import { emailService } from '@/services/email.service';
    import supabase from '@/supabase/supabase';

    // Standard dynamic imports
    const BusinessCampaignsPage = lazy(() => import('@/pages/business/BusinessCampaignsPage').then(m => ({ default: m.BusinessCampaignsPage })));
    const CreateCampaignPage = lazy(() => import('@/pages/business/CreateCampaignPage').then(m => ({ default: m.CreateCampaignPage })));
    const BusinessAnalyticsPage = lazy(() => import('@/pages/business/BusinessAnalyticsPage').then(m => ({ default: m.BusinessAnalyticsPage })));

    const DriverOverviewPage = lazy(() => import('@/pages/driver/DriverOverviewPage'));
    const DriverCampaignsPage = lazy(() => import('@/pages/driver/DriverCampaignsPage').then(m => ({ default: m.DriverCampaignsPage })));
    const DriverTasksPage = lazy(() => import('@/pages/driver/DriverTasksPage').then(m => ({ default: m.DriverTasksPage })));
    const DriverEarningsPage = lazy(() => import('@/pages/driver/DriverEarningsPage').then(m => ({ default: m.DriverEarningsPage })));
    const DriverVehiclesPage = lazy(() => import('@/pages/driver/DriverVehiclesPage'));
    const DriverPrintingShopsPage = lazy(() => import('@/pages/driver/DriverPrintingShopsPage').then(m => ({ default: m.DriverPrintingShopsPage })));

    const AdminOverviewPage = lazy(() => import('@/pages/admin/AdminOverviewPage'));
    const AdminCampaignsPage = lazy(() => import('@/pages/admin/AdminCampaignsPage'));
    const AdminApprovalsPage = lazy(() => import('@/pages/admin/AdminApprovalsPage').then(m => ({ default: m.AdminApprovalsPage })));
    const AdminDriversPage = lazy(() => import('@/pages/admin/AdminDriversPage').then(m => ({ default: m.AdminDriversPage })));
    const AdminBusinessesPage = lazy(() => import('@/pages/admin/AdminBusinessesPage').then(m => ({ default: m.AdminBusinessesPage })));
    const AdminPackagesPage = lazy(() => import('@/pages/admin/AdminPackagesPage').then(m => ({ default: m.AdminPackagesPage })));
    const AdminCountriesPage = lazy(() => import('@/pages/admin/AdminCountriesPage').then(m => ({ default: m.AdminCountriesPage })));
    const AdminPrintingShopsPage = lazy(() => import('@/pages/admin/AdminPrintingShopsPage').then(m => ({ default: m.AdminPrintingShopsPage })));
    const AdminStockOverviewPage = lazy(() => import('@/pages/admin/AdminStockOverviewPage').then(m => ({ default: m.AdminStockOverviewPage })));
    const ActivityLogPage = lazy(() => import('@/pages/admin/ActivityLogPage').then(m => ({ default: m.ActivityLogPage })));
    const PayoutsPage = lazy(() => import('@/pages/admin/PayoutsPage').then(m => ({ default: m.PayoutsPage })));
    const AdminEmailsPage = lazy(() => import('@/pages/admin/AdminEmailsPage').then(m => ({ default: m.AdminEmailsPage })));
    
    const PrintStatusPage = lazy(() => import('@/pages/public/PrintStatusPage').then(m => ({ default: m.PrintStatusPage })));

    const LoadingSpinner = () => (
      <div className="min-h-screen flex items-center justify-center dark:bg-slate-950">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

    let SESSION_WELCOME_LOCK = false;

    function DashboardRedirect() {
      const { profile, loading, user } = useAuth();
      
      useEffect(() => {
        if (!loading && profile && profile.email_verified && profile.welcome_sent === false && !SESSION_WELCOME_LOCK) {
          SESSION_WELCOME_LOCK = true;
          const triggerWelcome = async () => {
            const templateKey = profile.role === 'business' ? 'welcome_business' : 'welcome_driver';
            try {
              const { data: wasMarked } = await supabase.rpc('mark_welcome_sent', { p_user_id: profile.id });
              if (!wasMarked) return;
              
              const { data: templates } = await emailService.getTemplates();
              const template = templates?.find(t => t.template_key === templateKey);
              if (template) {
                const html = emailService.compileTemplate(template.html_content, { full_name: profile.full_name });
                await emailService.sendEmail({ to: profile.email, subject: template.subject, html });
              }
            } catch (err) {
              console.error('Welcome automation error:', err);
              SESSION_WELCOME_LOCK = false;
            }
          };
          triggerWelcome();
        }
      }, [profile, loading]);

      if (loading) return <LoadingSpinner />;
      if (user && !profile) return <LoadingSpinner />;
      if (profile?.role === 'admin') return <Navigate to="/dashboard/admin" replace />;
      if (profile?.role === 'driver') return <Navigate to="/dashboard/driver" replace />;
      if (profile?.role === 'business') return <Navigate to="/dashboard/business" replace />;
      return <Navigate to="/login" replace />;
    }

    function AppInner() {
      const navigate = useNavigate();

      useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'PASSWORD_RECOVERY') {
            toast.success('Recovery mode active. Set your new password.', { icon: '🔐' });
            navigate('/reset-password', { replace: true });
          }
        });
        return () => subscription.unsubscribe();
      }, [navigate]);

      return (
        <>
          <Toaster position="top-center" />
          <DebugConsole />
          
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Unauthenticated Routes */}
              <Route path="/print-status/:id" element={<PrintStatusPage />} />

              <Route element={<MainLayout />}>
                <Route path="/" element={<LandingPage />} />
              </Route>
              
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>

              <Route element={<ProtectedRoute />}>
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
              </Route>

              <Route path="/dashboard" element={<ProtectedRoute />}>
                <Route index element={<DashboardRedirect />} />
                
                <Route path="business" element={<ProtectedRoute allowedRoles={['business']} />}>
                  <Route element={<DashboardLayout />}>
                    <Route index element={<BusinessDashboard />} />
                    <Route path="campaigns" element={<BusinessCampaignsPage />} />
                    <Route path="campaigns/new" element={<CreateCampaignPage />} />
                    <Route path="analytics" element={<BusinessAnalyticsPage />} />
                  </Route>
                </Route>

                <Route path="driver" element={<ProtectedRoute allowedRoles={['driver']} />}>
                  <Route element={<DashboardLayout />}>
                    <Route index element={<DriverOverviewPage />} />
                    <Route path="campaigns" element={<DriverCampaignsPage />} />
                    <Route path="tasks" element={<DriverTasksPage />} />
                    <Route path="vehicles" element={<DriverVehiclesPage />} />
                    <Route path="earnings" element={<DriverEarningsPage />} />
                    <Route path="printing-shops" element={<DriverPrintingShopsPage />} />
                  </Route>
                </Route>

                <Route path="admin" element={<ProtectedRoute allowedRoles={['admin']} />}>
                  <Route element={<DashboardLayout />}>
                    <Route index element={<AdminOverviewPage />} />
                    <Route path="campaigns" element={<AdminCampaignsPage />} />
                    <Route path="approvals" element={<AdminApprovalsPage />} />
                    <Route path="drivers" element={<AdminDriversPage />} />
                    <Route path="businesses" element={<AdminBusinessesPage />} />
                    <Route path="emails" element={<AdminEmailsPage />} />
                    <Route path="packages" element={<AdminPackagesPage />} />
                    <Route path="payouts" element={<PayoutsPage />} />
                    <Route path="countries" element={<AdminCountriesPage />} />
                    <Route path="printing-shops" element={<AdminPrintingShopsPage />} />
                    <Route path="stock" element={<AdminStockOverviewPage />} />
                    <Route path="activity" element={<ActivityLogPage />} />
                  </Route>
                </Route>
              </Route>

              <Route path="/notifications" element={<ProtectedRoute />}>
                <Route element={<DashboardLayout />}>
                  <Route index element={<NotificationsPage />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </>
      );
    }

    export default function App() {
      return (
        <AuthProvider>
          <DebugProvider>
            <AppInner />
          </DebugProvider>
        </AuthProvider>
      );
    }